import { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLoader } from '../contexts/LoaderContext';
import { useAlert } from '../contexts/AlertContext';
import { users, UserRole, UsersResponse } from '../api/users';
import { Icons } from '../Components/Icons/Icons';
import { TABLE_COLUMNS } from '../Pages/Users/constants';
import { TableUser, TabId } from '../Pages/Users/types';

interface UseUsersListParams {
  itemsPerPage: number;
  onEditUser: (user: TableUser) => void;
  onDeleteUser: (id: number) => void;
}

const useUsersList = ({ itemsPerPage, onEditUser, onDeleteUser }: UseUsersListParams) => {
  const { userData } = useAuth();
  const { showLoader, hideLoader } = useLoader();
  const { showAlert } = useAlert();

  const [activeTab, setActiveTab] = useState<TabId>(
    userData?.role === UserRole.ADMIN ? 'agency_directors' : 'agency_marketing_department'
  );
  const [filterBy, setFilterBy] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [usersData, setUsersData] = useState<UsersResponse>({
    users: [],
    pageNumber: 1,
    pageSize: itemsPerPage,
    totalCount: 0,
    totalPages: 0,
  });

  const fetchUsers = useCallback(async () => {
    showLoader();
    try {
      const role = activeTab === 'agency_directors' ? UserRole.DIRECTOR : UserRole.USER;
      let agencyIds: string | undefined;

      if (filterBy) {
        agencyIds = filterBy;
      } else if (userData?.role !== UserRole.ADMIN && userData?.agencies) {
        agencyIds = userData.agencies.map((agency) => agency.id).join(',');
      }

      const response = await users.getList({
        limit: itemsPerPage,
        offset: (currentPage - 1) * itemsPerPage,
        role,
        ...(agencyIds ? { agencyIds } : {}),
      });
      setUsersData(response);
    } catch (error) {
      showAlert('Failed to load users', 'error');
    } finally {
      hideLoader();
    }
  }, [activeTab, currentPage, filterBy, itemsPerPage, userData, showAlert, showLoader, hideLoader]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleTabChange = useCallback((tabId: string) => {
    setActiveTab(tabId as TabId);
    setFilterBy('');
    setCurrentPage(1);
  }, []);

  const handleFilterChange = useCallback((value: string) => {
    setFilterBy(value);
    setCurrentPage(1);
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const columns = useMemo(
    () => [
      ...TABLE_COLUMNS,
      {
        id: 'action',
        label: 'Action',
        width: '30%',
        render: (row: TableUser) => (
          <div className="action-icons">
            <span
              className="edit-icon"
              onClick={(e) => {
                e.stopPropagation();
                onEditUser(row);
              }}
            >
              {Icons.Edit}
            </span>
            <span
              className="delete-icon"
              onClick={(e) => {
                e.stopPropagation();
                onDeleteUser(row.id);
              }}
            >
              {Icons.Delete}
            </span>
          </div>
        ),
      },
    ],
    [onEditUser, onDeleteUser]
  );

  return {
    activeTab,
    filterBy,
    currentPage,
    usersData,
    onTabChange: handleTabChange,
    onFilterChange: handleFilterChange,
    onPageChange: handlePageChange,
    columns,
  };
};

export default useUsersList;
